import { AddOutlined, CloseOutlined, ImageNotSupportedOutlined, SmartDisplayOutlined } from '@mui/icons-material'
import { EditorMedia, PostDraft, PostEditability, formatCount, inNetworkOrder, textLimitOf } from 'utils/socialCampaign/postEditor'
import { FC, ReactNode, useEffect, useId, useRef, useState } from 'react'
import { GRAY_700, GRAY_800 } from 'constants/styling/theme'
import { MediaFormat, SocialNetwork } from 'models/socialCampaign'

import { MUIInputField } from 'components/common/MUIInputField'
import { MUIRadioCard } from 'components/common/MUICards'
import { MediaThumb } from 'components/common/MediaThumb'
import { NetworkMark } from './NetworkMark.component'
import { SystemMessage } from 'components/common/SystemMessage'
import Typography from '@mui/material/Typography'
import styles from './PostEditorForm.module.sass'
import { useTranslation } from 'react-i18next'

interface Props {
  draft: PostDraft
  onChange: (change: Partial<PostDraft>) => void
  /** What the post still takes; everything when it is written by hand */
  editability: Pick<PostEditability, 'content' | 'schedule' | 'needsNewTime' | 'locked'>
  /** The campaign's networks: a post goes out on some of them */
  campaignNetworks: SocialNetwork[]
  /** Reserved for an order: only its date and time, and a note */
  waiting: boolean
  /** The date field's minimum, today in the campaign's timezone */
  minDay: string
  editFreezeMinutes: number
  onOpenPicker: () => void
  /** The AI actions under the text (ISSUE-3619, `PostTextActions`), when the post offers them */
  textActions?: ReactNode
  /**
   * An AI action is asked or rewrites the text: the text and the networks (what its answer is written from) wait; the
   * visuals and the time stay open (ISSUE-3619, #625's `textVersion`)
   */
  isTextHeld?: boolean
  /** The text is being rewritten: the field says it is busy */
  isTextBusy?: boolean
}

/** A visual of the strip, square; one the viewer's seat does not show (the backend signs no URL for it) stays in the post */
const Visual: FC<{ media: EditorMedia }> = ({ media }) => {
  const { t } = useTranslation(['social_campaign'])
  const video = media.format === MediaFormat.VIDEO
  const url = video ? media.previewUrl : media.thumbnailUrl ?? media.previewUrl

  return (
    <>

      {video
        ? <MediaThumb className={styles.thumb} kind="video" src={url ? `${url}#t=0.1` : undefined} alt="" kindIcon={<SmartDisplayOutlined />} />
        : (
          <MediaThumb
            className={styles.thumb}
            kind={media.format === MediaFormat.DOCUMENT ? 'pdf' : 'photo'}
            src={url ?? undefined}
            alt=""
            kindLabel={media.format === MediaFormat.DOCUMENT ? t('schedule.document') : undefined}
          />
        )
      }

      {!url &&
        <span className={styles.hidden} title={t('editor.visual_hidden')}><ImageNotSupportedOutlined /></span>
      }

    </>
  )
}

/**
 * The editor's form (prototype v3 `formHtml`): date and time (native inputs), the networks (the campaign's, untick one
 * for this post only), the visuals in the order they go out with remove and "+" (the gallery picker), then the one text
 * for every network with its count against the strictest ticked network, and the AI actions under it (ISSUE-3619). A
 * post reserved for an order shows its date (movable) and a note. TikTok's block (ISSUE-3630) comes with its ticket.
 */
export const PostEditorForm: FC<Props> = ({ draft, onChange, editability, campaignNetworks, waiting, minDay, editFreezeMinutes, onOpenPicker, textActions, isTextHeld = false, isTextBusy = false }) => {
  const { t, i18n } = useTranslation(['social_campaign'])
  const id = `campaign-editor-form-${useId().replace(/:/g, '')}`
  const language = i18n.resolvedLanguage || i18n.language
  const lastNetwork = draft.networks.length === 1
  const visualsRef = useRef<HTMLOListElement>(null)
  // removing a visual takes its button away: the focus goes to the next one's, else to "+" (prototype `ed-unmedia`)
  const [removedAt, setRemovedAt] = useState<number | null>(null)
  useEffect(() => {
    if (removedAt === null) return
    const buttons = visualsRef.current?.querySelectorAll<HTMLButtonElement>('button') ?? []
    buttons[Math.min(removedAt, buttons.length - 1)]?.focus()
    setRemovedAt(null)
  }, [removedAt, draft.media])

  const fieldLabel = (labelId: string, text: string) => (
    <Typography id={labelId} variant="text-sm" fontWeight={450} color={GRAY_800}>{text}</Typography>
  )

  const when = (
    <div className={styles.field}>

      {fieldLabel(`${id}-when`, t('editor.when'))}

      <div className={styles.when} role="group" aria-labelledby={`${id}-when`}>

        <MUIInputField
          inputElementType="date"
          inputAttributes={{ min: minDay }}
          inputAriaProps={{ 'aria-label': t('editor.date') }}
          value={draft.day}
          disabled={!editability.schedule}
          onChange={(event) => onChange({ day: event.target.value })}
        />

        <MUIInputField
          inputElementType="time"
          inputAttributes={{ step: 900 }}
          inputAriaProps={{ 'aria-label': t('editor.time') }}
          value={draft.time}
          disabled={!editability.schedule}
          onChange={(event) => onChange({ time: event.target.value })}
        />

      </div>

      {editability.needsNewTime && <Typography variant="text-xs" component="p" color={GRAY_700}>{t('editor.not_published')}</Typography>}

    </div>
  )

  const locked = editability.locked &&
    <SystemMessage variant="info" message={t(`editor.locked.${editability.locked}`, { minutes: editFreezeMinutes })} isRawContent />

  const limit = textLimitOf(draft.networks)
  const over = draft.text.length > limit.limit
  const count = t('editor.count', {
    typed: formatCount(draft.text.length, language),
    limit: formatCount(limit.limit, language),
    network: t(`network.${limit.network}`),
  })

  return (
    <>

      {locked}

      {when}

      {waiting && <SystemMessage variant="info" title={t('editor.waiting.title')} message={t('editor.waiting.body')} isRawContent />}

      {!waiting &&
        <div className={styles.field}>

          {fieldLabel(`${id}-networks`, t('editor.networks'))}

          <div className={styles.cards} role="group" aria-labelledby={`${id}-networks`}>
            {campaignNetworks.map((network) => {
              const ticked = draft.networks.includes(network)
              const fixed = !editability.content || isTextHeld || (ticked && lastNetwork)
              return (
                <MUIRadioCard
                  key={network}
                  name={`${id}-networks`}
                  value={network}
                  isMultiple
                  width="hug"
                  size="sm"
                  checked={ticked}
                  disabled={fixed}
                  aria-describedby={ticked && lastNetwork && editability.content ? `${id}-last-network` : undefined}
                  icon={<NetworkMark network={network} size={16} />}
                  label={t(`network.${network}`)}
                  // a disabled input can still be toggled by a scripted click: the form holds its value itself
                  onChange={() => !fixed && onChange({
                    networks: ticked ? draft.networks.filter((item) => item !== network) : inNetworkOrder([...draft.networks, network]),
                  })}
                />
              )
            })}
          </div>

          {lastNetwork && editability.content &&
            <Typography id={`${id}-last-network`} variant="text-xs" component="p" color={GRAY_700}>{t('editor.last_network')}</Typography>
          }

        </div>
      }

      {!waiting &&
        <div className={styles.field}>

          {fieldLabel(`${id}-visuals`, t('editor.visuals'))}

          <ol ref={visualsRef} className={styles.media} aria-labelledby={`${id}-visuals`}>
            {draft.media.map((media, index) => (
              <li key={`${media.key.assignmentPublicId}/${media.key.visualType}/${media.key.filename}`} className={styles.tile}>

                <Visual media={media} />

                <span className={styles.number} aria-hidden="true">{index + 1}</span>

                {editability.content &&
                  <button
                    type="button"
                    className={styles.remove}
                    aria-label={t('editor.remove_visual', { index: index + 1 })}
                    onClick={() => {
                      onChange({ media: draft.media.filter((_, at) => at !== index) })
                      setRemovedAt(index)
                    }}
                  >
                    <CloseOutlined />
                  </button>
                }

              </li>
            ))}
            {editability.content &&
              <li>
                <button type="button" className={styles.add} aria-label={t('editor.add_visuals')} onClick={onOpenPicker}>

                  <AddOutlined />

                  <span aria-hidden="true">{t('editor.add')}</span>

                </button>
              </li>
            }
          </ol>

        </div>
      }

      {!waiting &&
        <MUIInputField
          isMultiline
          rows={7}
          label={t('editor.text')}
          inputAriaProps={{ 'aria-label': t('editor.text'), ...(isTextBusy && { 'aria-busy': true }) }}
          value={draft.text}
          disabled={!editability.content || isTextHeld}
          isError={over}
          hintText={count}
          errorText={count}
          onChange={(event) => onChange({ text: event.target.value })}
        />
      }

      {textActions}
    </>
  )
}
